"use client";
import { createContext, useContext, ReactNode, useState, useMemo } from "react";
import type { Merchant } from "@/dto/merchant";

interface MerchantContextType {
  merchant: Merchant;
  // eslint-disable-next-line no-unused-vars
  setMerchant: (merchant: Merchant) => void;
}

const MerchantContext = createContext<MerchantContextType | undefined>(
  undefined
);

interface MerchantProviderProps {
  children: ReactNode;
  initialMerchant: Merchant;
}

export function MerchantProvider({
  children,
  initialMerchant,
}: MerchantProviderProps) {
  const [merchant, setMerchant] = useState<Merchant>(initialMerchant);

  const value = useMemo<MerchantContextType>(() => ({
    merchant,
    setMerchant,
  }), [merchant]);

  return (
    <MerchantContext.Provider value={value}>
      {children}
    </MerchantContext.Provider>
  );
}

/**
 * Custom hook for accessing and updating the current merchant data.
 *
 * This hook consumes the {@link MerchantContext} and provides the current
 * merchant object and a setter function to modify it.
 *
 * @returns {MerchantContextType} An object containing the current {@link Merchant} state and the setter function.
 * @throws {Error} If called outside of a {@link MerchantProvider}.
 *
 */
export function useMerchant() {
  const context = useContext(MerchantContext);

  if (context === undefined) {
    throw new Error("useMerchant must be used within a MerchantProvider");
  }

  return context;
}

export type { MerchantContextType };
